// src/pages/providers/2_dashboard/components/PatientTable.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon, InformationCircleIcon, PlusCircleIcon, ExclamationIcon } from '@heroicons/react/solid';
import { TrashIcon, UserIcon, CalendarIcon, DocumentDuplicateIcon, ArchiveIcon,
  // PencilIcon
 } from '@heroicons/react/outline';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import StatusMessage from '../../../../components/StatusMessage';
import PatientRemoveForm from './PatientRemoveForm';
import PatientArchiveDialog from './PatientArchiveDialog';
import AssignCliniciansDialog from '../../5_patientOverview/components/AssignCliniciansDialog';
// import PatientEditForm from './PatientEditForm';
import { Tooltip } from 'react-tooltip';
import { formatDateShort, compareTimestamps } from '../../../../utils/timestampUtils';
import { resolveWorkflowConfig } from '../../../../hooks/useWorkflowConfig';
import { CHIP_FILTERS } from '../../../../utils/patientFilterUtils';
import { matchesFullName, matchesEmail, matchesDateAddedRange } from '../../../../utils/personFilterUtils';
import { matchesAssignedClinician } from '../../../../utils/assignedClinicianFilter';
import { isSlamOrg } from '../config/patientSetupConfig';
import { orgShowsDashboardContractColumn } from '../../../../constants/assessmentTypesByOrg';
import PatientContractTypeBadge from '../../../../components/PatientContractTypeBadge';
import useSessionState from '../../../../hooks/useSessionState';
import { DASHBOARD_KEYS } from '../../../../utils/dashboardViewState';
import CondensedPagination from '../../../../components/CondensedPagination';
import DashboardPersonName from './DashboardPersonName';
import {
  getPatientOverviewPath,
  getPatientOverviewNavState,
} from '../utils/patientOverviewRoute';
import { emitCaseloadPatientOpened } from '../../../../analytics/caseloadNavEvents';

function PatientTable({ activeFilters, activeChip = null, onRecordPatientView }) {
  const { patientsData, patientsLoading, userData, refreshPatientsData } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  // Column sort order is session-persisted so it survives navigating to a patient
  // and back. It's a pure view transform over patientsData, so restoring it just
  // re-applies the same ordering to the (already up-to-date) list. The same sort
  // applies whether or not a quick-filter chip is active — chips only filter, they
  // do not change the sort — so the displayed order always matches sortConfig (and
  // therefore the column's sort icon).
  const [sortConfig, setSortConfig] = useSessionState(
    DASHBOARD_KEYS.sort,
    () => ({ key: 'createdAt', direction: 'descending' })
  );
  const [removingPatient, setRemovingPatient] = useState(null);
  const [archivingPatient, setArchivingPatient] = useState(null);
  // const [editingPatient, setEditingPatient] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [assigningPatientId, setAssigningPatientId] = useState(null);

  // Only Org. Admins and Support users can perform initial clinician assignment.
  // Regular Clinicians see a read-only "Not assigned yet" label instead of the
  // actionable button. Backend enforces this as defence-in-depth via auth claims.
  const canAssignClinicians = userData?.userType === 'Org. Admin' || userData?.userType === 'Support';
  const [clickedButton, setClickedButton] = useState(null);
  const patientsPerPage = 10;

  // TOC (and Demo Org sandbox) get an extra Contract column after Pathway.
  // Other orgs keep the original layout so we do not spend horizontal space
  // on a column they will never populate.
  const showContractColumn = orgShowsDashboardContractColumn(userData?.organisation);

  // Reset pagination when filters or chip change to avoid landing on an empty
  // page. We intentionally do NOT persist currentPage, so this also runs on
  // mount to start each visit on page 1 (after any optimistic add/remove the
  // list may have changed size, so page 1 is the safe landing spot).
  useEffect(() => {
    setCurrentPage(1);
  }, [activeChip, activeFilters]);

  // Stage key → short name mapping for progressByStage lookup
  const STAGE_SHORT_NAMES = {
    '1_preAppointmentInformation': 'preQ',
    '2_clinicalInterview': 'ci',
    '3_semiStructuredInterview': 'ssi',
    '4_diagnosticDecision': 'dd',
    '5_postAssessmentDiscussion': 'pad',
  };

  // Read pre-computed progress from the patient summary.
  // Progress and lock states are computed server-side by Cloud Functions
  // (patient_summary_functions.py) and stored in the patient_summaries collection.
  const calculateStageProgress = (patient, stageKey) => {
    const key = STAGE_SHORT_NAMES[stageKey];
    if (key && patient?.progressByStage && typeof patient.progressByStage[key] !== 'undefined') {
      return patient.progressByStage[key];
    }
    return 0;
  };

  // Stage progress indicator component
  const AssessmentProgress = ({ patient }) => {
    const module = patient.assessmentModule || 'adultAdhd';
    const isModular = module === 'adultAutism' || module === 'cypAdhd';

    // Modular patients (Autism, CYP ADHD) use subcollection-based assessment stages.
    // Progress values are computed by *_stage_progress.py and stored in patient_summaries.
    // A value of -1 means the stage is locked (same sentinel as ADHD).
    if (isModular) {
      // Behavioural Observation is optional. We data-gate its pill on the
      // presence of an `obs` entry in the server-computed progressByStage rather
      // than the org name: orgs that disable Observation (e.g. LMP) simply have
      // no `obs` key, so the pill is dropped without any org-specific branching.
      // TOC and every existing autism patient keep the full five-pill row.
      //
      // CYP ADHD gets its OWN row, not autism's: CI and SSI are separate pills
      // under their CYP names (cyp_adhd_stage_progress.py emits ci/ssi, not
      // autism's combined ddi), matching the adult ADHD row a clinician sees on
      // the very next line of this table. Keying CYP off "modular" was the same
      // wrong-condition bug as the clinician tooltip below (commit c7c30517):
      // a CYP row showed autism's stage names and one fused pill where adult
      // shows two.
      const autismStages = module === 'cypAdhd'
        ? [
          { key: 'forms', name: 'Pre-Assessment Questionnaires' },
          { key: 'ci', name: 'Clinical Interview' },
          { key: 'ssi', name: 'Semi-Structured Interview' },
          { key: 'dd', name: 'Diagnostic Decision' },
          { key: 'pad', name: 'Post-Assessment Discussion' },
        ]
        : [
          { key: 'forms', name: 'Pre-Assessment Questionnaires' },
          { key: 'ddi', name: 'Diagnostic Developmental History' },
          { key: 'obs', name: 'Behavioural Observation' },
          { key: 'dd', name: 'MDT Review' },
          { key: 'pad', name: 'Feedback Session' },
        ].filter(
          (stage) => stage.key !== 'obs' || patient?.progressByStage?.obs !== undefined,
        );

      return (
        <div className="flex items-center space-x-1">
          {autismStages.map((stage) => {
            const progress = patient?.progressByStage?.[stage.key] ?? 0;
            let backgroundColor = 'bg-gray-200';
            let fillColor = '';
            let tooltipText = '';

            if (progress === -1) {
              backgroundColor = 'bg-gray-500';
              tooltipText = `${stage.name}: Locked`;
            } else if (progress === 100) {
              backgroundColor = 'bg-green-500';
              tooltipText = `${stage.name}: ${progress}% Complete`;
            } else if (progress > 0) {
              backgroundColor = 'bg-gray-200';
              fillColor = 'bg-amber-400';
              tooltipText = `${stage.name}: ${progress}% complete`;
            } else {
              tooltipText = `${stage.name}: Not started`;
            }

            return (
              <div
                key={stage.key}
                className="relative text-xs"
                data-tooltip-id={`progress-${patient.patientId}-${stage.key}`}
              >
                <div className={`h-3 w-8 rounded-sm ${backgroundColor} overflow-hidden`}>
                  {progress > 0 && progress < 100 && (
                    <div
                      className={fillColor}
                      style={{
                        width: `${progress}%`,
                        height: '100%'
                      }}
                    ></div>
                  )}
                </div>
                <Tooltip
                  id={`progress-${patient.patientId}-${stage.key}`}
                  style={{
                    maxWidth: '250px',
                    whiteSpace: 'normal',
                    backgroundColor: 'rgba(34, 34, 34, 1)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: "bold"
                  }}
                  place="top"
                  content={tooltipText}
                />
              </div>
            );
          })}
        </div>
      );
    }

    // ADHD patients: use the existing stage keys with SSI-first awareness.
    // Determine the display ordering of stages.
    // SSI-first patients show SSI before CI (SSI → CI) to reflect their workflow.
    // Everyone else uses the default ordering (CI → SSI).
    //
    // The order is a per-PATIENT fact, not a per-org one: SLaM has legacy
    // two-clinician SSI-first patients and new one-clinician CI-first patients
    // side by side on the same dashboard. The server-computed summary carries
    // `workflowModuleOrder` ('ssiFirst' | 'ciFirst') derived from the patient's
    // stamped (or legacy) config, so prefer that. Summaries written before the
    // field existed fall back to the org preset, exactly as before. (Run the
    // patient-summary backfill after deploy so historical rows carry their
    // own order and are not visually rewritten by a preset change.)
    const isSSIFirst = patient?.workflowModuleOrder
      ? patient.workflowModuleOrder === 'ssiFirst'
      : resolveWorkflowConfig(null, { organisation: patient?.organisation }).isSSIFirst;
    const stages = isSSIFirst
      ? [
          { key: '1_preAppointmentInformation', name: 'Pre-Assessment Questionnaires' },
          { key: '3_semiStructuredInterview', name: 'Semi-Structured Interview' },
          { key: '2_clinicalInterview', name: 'Clinical Interview' },
          { key: '4_diagnosticDecision', name: 'Diagnostic Decision' },
          { key: '5_postAssessmentDiscussion', name: 'Post-Assessment Discussion' }
        ]
      : [
      { key: '1_preAppointmentInformation', name: 'Pre-Assessment Questionnaires' },
      { key: '2_clinicalInterview', name: 'Clinical Interview' },
      { key: '3_semiStructuredInterview', name: 'Semi-Structured Interview' },
      { key: '4_diagnosticDecision', name: 'Diagnostic Decision' },
      { key: '5_postAssessmentDiscussion', name: 'Post-Assessment Discussion' }
    ];

    // Shown beside the progress pills when this assessment was ended after
    // the Clinical Interview. Kept as one string so the hover tooltip and
    // the icon's accessible name always say the same thing.
    const assessmentEndedTooltip = patient?.assessmentEndedAfterCi === true
      ? (patient?.assessmentEndedAt
        ? `Assessment ended after the Clinical Interview on ${formatDateShort(patient.assessmentEndedAt, '')}. Open the patient to see the reason or to resume.`
        : 'Assessment ended after the Clinical Interview. Open the patient to see the reason or to resume.')
      : '';

    return (
      <div className="flex items-center space-x-1">
        {stages.map((stage) => {
          const progress = calculateStageProgress(patient, stage.key);
          let backgroundColor = 'bg-gray-200';
          let fillColor = '';
          let tooltipText = '';
          
          if (progress === -1) {
            // Locked stage
            backgroundColor = 'bg-gray-500';
            tooltipText = `${stage.name}: Locked`;
          } else if (progress === 100) {
            // Completed stage
            backgroundColor = 'bg-green-500';
            tooltipText = `${stage.name}: ${progress}% Complete`;
          } else if (progress > 0) {
            // In progress - amber color
            backgroundColor = 'bg-gray-200';
            fillColor = 'bg-amber-400';
            tooltipText = `${stage.name}: ${progress}% complete`;
          } else {
            // Not started
            tooltipText = `${stage.name}: Not started`;
          }
          
          return (
            <div 
              key={stage.key}
              className="relative text-xs"
              data-tooltip-id={`progress-${patient.patientId}-${stage.key}`}
            >
              <div className={`h-3 w-8 rounded-sm ${backgroundColor} overflow-hidden`}>
                {progress > 0 && progress < 100 && (
                  <div 
                    className={fillColor} 
                    style={{ 
                      width: `${progress}%`,
                      height: '100%' 
                    }}
                  ></div>
                )}
              </div>                                      
              <Tooltip 
                id={`progress-${patient.patientId}-${stage.key}`}
                style={{
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  backgroundColor: 'rgba(34, 34, 34, 1)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontWeight: "bold"
                }}
                place="top"
                content={tooltipText}
              />
            </div>
          );
        })}
        {/* One-clinician SLaM workflow: the Lead chose "End assessment" after the
            Clinical Interview. The SSI/DD/PAD pills above already render as locked
            (progress -1 from the server), but a locked pill alone does not say WHY.
            A text chip here made the progress column wide enough that the rest of
            the table had to scroll, so the marker is just the amber exclamation
            icon. Hover (and keyboard focus) shows the explanation, including the
            date when we have one. The same words are the icon's accessible name,
            so the meaning is not carried by colour alone. It is reversible from
            the Patient Overview ("Resume assessment"). Driven by the summary's
            assessmentEndedAfterCi flag so no full patient read is needed. */}
        {patient?.assessmentEndedAfterCi === true && (
          <>
            <span
              className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-100 text-amber-800"
              data-tooltip-id={`assessment-ended-${patient.patientId}`}
              data-testid={`assessment-ended-icon-${patient.patientId}`}
              role="img"
              tabIndex={0}
              aria-label={assessmentEndedTooltip}
            >
              <ExclamationIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <Tooltip
              id={`assessment-ended-${patient.patientId}`}
              style={{
                maxWidth: '250px',
                whiteSpace: 'normal',
                backgroundColor: 'rgba(34, 34, 34, 1)',
                borderRadius: '10px',
                color: '#fff',
                fontWeight: "bold"
              }}
              place="top"
              content={assessmentEndedTooltip}
            />
          </>
        )}
      </div>
    );
  };

  // Apply filters to patients data (exclude archived patients)
  const filteredPatients = useMemo(() => {
    const baseFiltered = patientsData.filter(patient => {
      // Skip archived patients in the main table
      if (patient.isArchived === true) {
        return false;
      }
      // Name / email / date-added matching is shared with the staff table via
      // personFilterUtils so both apply identical (and separately-tested) rules.
      const nameMatch = matchesFullName(patient, activeFilters.patientName);
      const emailMatch = matchesEmail(patient, activeFilters.emailAddress);
      const dateMatch = matchesDateAddedRange(patient, activeFilters.dateRange, 'createdAt');

      // Reference number filter
      const referenceMatch = !activeFilters.referenceNumber ||
      (patient.customFields?.referenceNumber || '').toLowerCase()
        .includes(activeFilters.referenceNumber.toLowerCase());

      // Payment method filter
      const paymentMatch = !activeFilters.paymentMethod ||
        patient.customFields?.paymentMethod === activeFilters.paymentMethod.value;

      // Contract Type filter (Well Life Clinic / The Owl Centre — labelled "Contract Type"
      // in the UI but stored as assessmentType in the backend)
      const assessmentTypeMatch = !activeFilters.assessmentType ||
        patient.customFields?.assessmentType === activeFilters.assessmentType.value;

      // Assessment module filter (Adult ADHD vs Adult Autism).
      // Missing assessmentModule defaults to 'adultAdhd' for backwards compatibility.
      const moduleMatch = !activeFilters.assessmentModule ||
        (patient.assessmentModule || 'adultAdhd') === activeFilters.assessmentModule.value;

      // Assigned-to filter. Clinician viewers already only have their own
      // caseload in patientsData, so this is an AND with that existing scope.
      const assignedToMatch = matchesAssignedClinician(
        patient,
        activeFilters.assignedTo?.value
      );

      // All conditions must be true
      return nameMatch && emailMatch && paymentMatch && assessmentTypeMatch && moduleMatch && dateMatch && referenceMatch && assignedToMatch;
    });

    // Apply quick-filter chip predicate if one is active
    if (activeChip && CHIP_FILTERS[activeChip]) {
      return baseFiltered.filter(CHIP_FILTERS[activeChip].predicate);
    }
    return baseFiltered;
  }, [patientsData, activeFilters, activeChip]);

  // Total non-archived patients — used in the contextual note to show
  // "Showing X of Y patients" so the user understands the filtered subset
  // relative to the full population.
  const totalNonArchivedCount = useMemo(
    () => patientsData.filter(p => p.isArchived !== true).length,
    [patientsData]
  );

  const sortedPatients = useMemo(() => {
    // Sorting is driven purely by sortConfig and applies regardless of whether a
    // chip is active (chips only filter the rows). This keeps the displayed order
    // in lock-step with the column sort icon and makes a single header click flip
    // the order as expected.
    let sortablePatients = [...filteredPatients];
    if (sortConfig.key) {
      sortablePatients.sort((a, b) => {
        // Date Added prints only the calendar day, but the stored createdAt
        // still has a clock time. Sort by that full instant so the patient
        // added latest today is actually first when the column is newest-first.
        if (sortConfig.key === 'createdAt') {
          return compareTimestamps(a.createdAt, b.createdAt, sortConfig.direction);
        }

        // Handle reference number in customFields
        if (sortConfig.key === 'referenceNumber') {
          const aValue = a.customFields?.referenceNumber || '';
          const bValue = b.customFields?.referenceNumber || '';
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        // Handle payment method in customFields
        if (sortConfig.key === 'paymentMethod') {
          const aValue = a.customFields?.paymentMethod || '';
          const bValue = b.customFields?.paymentMethod || '';
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // Handle other fields
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        return sortConfig.direction === 'ascending'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    }
    return sortablePatients;
  }, [filteredPatients, sortConfig]);

  // Clamp currentPage when the filtered list shrinks (e.g. after a clinician
  // assignment removes a patient from the active chip filter). Without this,
  // the user could land on an empty page if the last patient on that page
  // was the one just assigned.
  useEffect(() => {
    const totalPages = Math.ceil(sortedPatients.length / patientsPerPage);
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [sortedPatients.length, currentPage]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    // Always reserve space to prevent header width jank
    if (sortConfig.key !== columnKey) {
      return <span className="inline-block w-4 h-4 ml-1" aria-hidden="true"></span>;
    }
    return sortConfig.direction === 'ascending'
      ? <ChevronUpIcon className="w-4 h-4 ml-1 text-white" />
      : <ChevronDownIcon className="w-4 h-4 ml-1 text-white" />;
  };

  const copyPatientIdToClipboard = async (patientId) => {
    try {
      await navigator.clipboard.writeText(patientId);
      
      // Show blue button state temporarily
      setClickedButton(patientId);
      setTimeout(() => setClickedButton(null), 300); // Blue for 300ms
      
      // Show toast notification
      toast.success(`Patient ID ${patientId} has been copied to clipboard`);
      
    } catch (err) {
      console.error('Failed to copy patient ID:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = patientId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      // Show blue button state temporarily
      setClickedButton(patientId);
      setTimeout(() => setClickedButton(null), 300);
      
      // Show toast notification
      toast.success(`Patient ID ${patientId} has been copied to clipboard`);
    }
  };

  if (sortedPatients.length === 0) {
    if (patientsLoading) {
      return (
        <div className="w-full h-24 flex items-center justify-center">
          <div className="inline-flex items-center px-4 py-2 bg-violet-100 text-slate-800 rounded-lg">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-slate-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading patients...</span>
          </div>
        </div>
      );
    }
    return (
      <StatusMessage
        color="gray"
        icon={<InformationCircleIcon className="w-5 h-5 mr-2" />}
      >
        {activeChip && CHIP_FILTERS[activeChip]
          ? CHIP_FILTERS[activeChip].emptyMessage
          : 'No patient records available. Remove any search filters or click "Set Up New Patient" to create a new record.'
        }
      </StatusMessage>
    );
  }

  // Helper function to get unique clinicians with their stages
  const getUniqueClinicians = (patient) => {
    const cliniciansList = [];
    const clinicianMap = {};
    
    if (!patient.clinicians) return cliniciansList;
    
    const isAutismPatient = patient.assessmentModule === 'adultAutism';

    // Keyed on AUTISM specifically, not on "is modular". "History" and
    // "Feedback Session" are Autism's names for these stages; CYP ADHD calls
    // them Clinical Interview and Post-Assessment Discussion
    // (CYP_ADHD_STAGE_NAMES in utils/stageDisplayNames.js). Testing for
    // modular showed a CYP row its clinicians under Autism's stage names,
    // while the sibling ArchivedPatientsTable — same rows, same clinicians —
    // showed the CYP names. Two names for one stage, in two places one
    // clinician sees on the same dashboard.
    //
    // Autism's labels here are deliberately left exactly as they are, DD
    // included, even though this table calls DD "Diagnostic Decision" where
    // the canonical map calls it "MDT Review". Correcting that is a change to
    // an adult pathway and belongs on its own ticket.
    const stages = {
      'clinicianCI': { code: 'CI', name: isAutismPatient ? 'History' : 'Clinical Interview' },
      'clinicianSSI': { code: 'SSI', name: 'Semi-Structured Interview' },
      'clinicianDD': { code: 'DD', name: 'Diagnostic Decision' },
      'clinicianPAD': { code: 'PAD', name: isAutismPatient ? 'Feedback Session' : 'Post-Assessment Discussion' },
    };
    // OBS stage only exists for autism patients (not CYP ADHD)
    if (isAutismPatient) {
      stages['clinicianOBS'] = { code: 'OBS', name: 'Behavioural Observation' };
    }
    
    // Add primary clinicians
    Object.entries(stages).forEach(([stageKey, stageInfo]) => {
      const clinician = patient.clinicians[stageKey];
      if (clinician && clinician.clinicianId) {
        if (!clinicianMap[clinician.clinicianId]) {
          clinicianMap[clinician.clinicianId] = {
            clinicianId: clinician.clinicianId,
            clinicianName: clinician.clinicianName,
            stages: [stageInfo]
          };
        } else {
          clinicianMap[clinician.clinicianId].stages.push(stageInfo);
        }
      }
    });
    
    // Add supporting clinicians for Diagnostic Decision stage
    const ddClinician = patient.clinicians?.clinicianDD;
    if (ddClinician && ddClinician.supportingClinicianIds && 
        ddClinician.supportingClinicianIds.length > 0 &&
        ddClinician.supportingClinicianNames &&
        ddClinician.supportingClinicianNames.length > 0) {
      
      const ddStageInfo = { code: 'DD', name: 'Diagnostic Decision (MDT Review)' };
      
      for (let i = 0; i < ddClinician.supportingClinicianIds.length; i++) {
        const clinicianId = ddClinician.supportingClinicianIds[i];
        const clinicianName = ddClinician.supportingClinicianNames[i] || 'Unknown Clinician';
        
        if (!clinicianMap[clinicianId]) {
          clinicianMap[clinicianId] = {
            clinicianId: clinicianId,
            clinicianName: clinicianName,
            stages: [ddStageInfo]
          };
        } else {
          // If already in our map (e.g., they're primary for another stage),
          // just add DD supporting role
          clinicianMap[clinicianId].stages.push(ddStageInfo);
        }
      }
    }
    
    // Convert map to array
    Object.values(clinicianMap).forEach(clinician => {
      cliniciansList.push(clinician);
    });
    
    return cliniciansList;
  };

  // Pagination logic
  const indexOfLastPatient = currentPage * patientsPerPage;
  const indexOfFirstPatient = indexOfLastPatient - patientsPerPage;
  const currentPatients = sortedPatients.slice(indexOfFirstPatient, indexOfLastPatient);
  
  const totalPages = Math.ceil(sortedPatients.length / patientsPerPage);

  return (
    <div className="w-full space-y-4">
      {/* Outer wrapper: border + shadow applied here so pagination sits inside the card */}
      <div className="relative flex flex-col w-full h-full text-gray-700 bg-white border border-slate-200 shadow-sm rounded-lg isolate">
      {/* Scrollable table region only — pagination stays outside this div */}
      <div className="overflow-auto">
        <table className="w-full text-left table-auto min-w-max">
        <thead>
            <tr>             
              <th 
                className={`p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 ${showContractColumn ? 'w-[220px]' : 'w-[250px]'} sticky left-0 z-20 after:absolute after:top-0 after:left-full after:h-full after:w-4 after:bg-gradient-to-r after:from-black/[0.10] after:to-transparent after:pointer-events-none after:content-['']`}
                onClick={() => requestSort('firstName')}
              >
                <div className="flex items-center">
                  <p className="text-xs font-medium text-white">
                    Patient Name
                  </p>
                  {getSortIcon('firstName')}
                </div>
              </th>
              <th 
                className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 w-[90px]"
                onClick={() => requestSort('referenceNumber')}
              >
                <div className="flex items-center">
                  <p className="text-xs font-medium text-white">
                    Reference
                  </p>
                  {getSortIcon('referenceNumber')}
                </div>
              </th>
              <th 
                className={`p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 ${showContractColumn ? 'w-[140px]' : 'w-[160px]'}`}
                onClick={() => requestSort('createdAt')}
              >
                <div className="flex items-center">
                  <p className="text-xs font-medium text-white">
                    Date Added
                  </p>
                  {getSortIcon('createdAt')}
                </div>
              </th>
              <th className={`p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 ${showContractColumn ? 'w-[130px]' : 'w-[155px]'}`}>
                <p className="text-xs font-medium text-white">
                  Pathway
                </p>
              </th>
              {showContractColumn && (
                <th className="p-3 border-b border-[#43213e] bg-[#43213e] w-[180px]">
                  <p className="text-xs font-medium text-white">
                    Contract
                  </p>
                </th>
              )}
              <th className={`p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 ${showContractColumn ? 'w-[260px]' : 'w-[340px]'}`}>
                <p className="text-xs font-medium text-white">
                  Assigned Clinicians
                </p>
              </th>
              <th className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200">
                <p className="text-xs font-medium text-white">
                  Progress
                </p>
              </th>
              {/* <th className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200">
                <p className="text-xs font-medium text-slate-700">
                  Status
                </p>
              </th> */}
              <th className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200">
                <p className="text-xs font-medium text-white">
                  Actions
                </p>
              </th>
            </tr>
          </thead>
            <tbody>
              {sortedPatients.length === 0 ? (
                <tr>
                  <td colSpan={showContractColumn ? 8 : 7} className="px-6 py-4">
                    <div className="flex items-center justify-center bg-gray-100 text-gray-700 p-4 rounded-md text-sm">
                      <InformationCircleIcon className="w-5 h-5 mr-2" />
                      {activeChip && CHIP_FILTERS[activeChip]
                        ? CHIP_FILTERS[activeChip].emptyMessage
                        : 'No patient records available.'
                      }
                    </div>
                  </td>
                </tr>
              ) : (
                currentPatients.map((patient) => {
                  // Path + nav state are shared by the name link and the View
                  // Patient button so the two entry points cannot drift.
                  const overviewPath = getPatientOverviewPath(patient);
                  const overviewState = getPatientOverviewNavState(patient);
                  const displayName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();

                  return (
                    <tr key={patient.patientId} className="group hover:bg-slate-50 border-b border-slate-200">
                      <td className={`px-3 py-2 ${showContractColumn ? 'w-[220px]' : 'w-[250px]'} sticky left-0 z-10 bg-white group-hover:bg-slate-50 after:absolute after:top-0 after:left-full after:h-full after:w-4 after:bg-gradient-to-r after:from-black/[0.07] after:to-transparent after:pointer-events-none after:content-['']`}>
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
                            <UserIcon className="h-4 w-4 text-violet-800" />
                          </div>
                          <div className="min-w-0 flex-1">
                            {overviewPath ? (
                              <DashboardPersonName
                                to={overviewPath}
                                state={overviewState}
                                ariaLabel={`View patient overview for ${displayName || 'this patient'}`}
                                onClick={() => {
                                  emitCaseloadPatientOpened({
                                    patientId: patient.patientId,
                                    trigger: 'name',
                                    caseload: 'current',
                                  });
                                  // Record for the recently-viewed shortcut on the dashboard
                                  onRecordPatientView?.(patient);
                                }}
                              >
                                {patient.firstName} {patient.lastName}
                              </DashboardPersonName>
                            ) : (
                              <p className="block font-semibold text-xs text-slate-800 truncate">
                                {patient.firstName} {patient.lastName}
                              </p>
                            )}
                            <p className="text-xs text-slate-500 truncate">
                              {/* CYP ADHD children have no account and no email
                                  address, so this line renders an em dash as an
                                  interim. What this cell should finally show for
                                  CYP rows (parent/carer email vs a parent-name
                                  column vs blank) is an open decision with Sai. */}
                              {patient.assessmentModule === 'cypAdhd' ? '—' : patient.email}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-2 text-xs text-slate-600 w-[90px]">
                        <div className="truncate">
                        {patient.customFields?.referenceNumber || '-'}
                        </div>
                      </td>

                      <td className={`px-3 py-2 text-xs ${showContractColumn ? 'w-[140px]' : 'w-[160px]'}`}>
                      <div className="flex items-center space-x-2"
                       data-tooltip-id={`creator-${patient.patientId}`}>
                      <CalendarIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <p className="text-xs text-slate-600 whitespace-nowrap">
                        {formatDateShort(patient.createdAt, 'N/A')}
                      </p>
                    </div>
                        <Tooltip
                          id={`creator-${patient.patientId}`}
                          place="top"
                          style={{
                            maxWidth: '250px',
                            whiteSpace: 'normal',
                            backgroundColor: 'rgba(34, 34, 34, 1)',
                            borderRadius: '10px',
                            color: '#fff',
                            fontWeight: "bold"
                          }}
                        >
                          Added by {patient.createdByName || 'Unknown'}
                        </Tooltip>
                      </td>


                      <td className={`px-3 py-2 ${showContractColumn ? 'w-[130px]' : 'w-[155px]'}`}>
                        {(() => {
                          const module = patient.assessmentModule || 'adultAdhd';
                          const chipConfig = {
                            adultAdhd: { bg: 'bg-blue-100 text-blue-800', label: 'Adult ADHD' },
                            adultAutism: { bg: 'bg-teal-100 text-teal-800', label: 'Adult Autism' },
                            cypAdhd: { bg: 'bg-purple-100 text-purple-800', label: 'CYP ADHD' },
                          };
                          const { bg, label } = chipConfig[module] || chipConfig.adultAdhd;
                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg}`}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>

                      {showContractColumn && (
                        <td className="px-3 py-2 w-[180px]">
                          <PatientContractTypeBadge
                            stack
                            organisation={userData?.organisation}
                            assessmentType={patient.customFields?.assessmentType}
                            subContractType={patient.customFields?.subContractType}
                          />
                        </td>
                      )}

                     <td className={`px-3 py-2 text-xs ${showContractColumn ? 'w-[260px]' : 'w-[340px]'}`}>
  <div className="flex flex-wrap items-center gap-2">
    {getUniqueClinicians(patient).length === 0 ? (
      canAssignClinicians ? (
        <button
          type="button"
          onClick={() => setAssigningPatientId(patient.patientId)}
          aria-label={`Assign clinician to ${patient.firstName} ${patient.lastName}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium
            border border-secondary bg-white text-primary
            hover:bg-secondary/10 hover:border-secondary
            transition-colors duration-200 cursor-pointer"
        >
          <PlusCircleIcon className="w-4 h-4 text-secondary" />
          Assign clinician
        </button>
      ) : (
        <span className="text-amber-600 text-xs italic">
          Not assigned yet
        </span>
      )
    ) : (
      <>
        {getUniqueClinicians(patient).map((clinician) => (
          <div key={`clinician-${patient.patientId}-${clinician.clinicianId}`}>
            <span
              data-tooltip-id={`clinician-${patient.patientId}-${clinician.clinicianId}`}
              className="border inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200 cursor-help"
            >
              {clinician.clinicianName}
            </span>
            <Tooltip
              id={`clinician-${patient.patientId}-${clinician.clinicianId}`}
              place="top"
              style={{
                maxWidth: '250px',
                whiteSpace: 'normal',
                backgroundColor: 'rgba(34, 34, 34, 1)',
                borderRadius: '10px',
                color: '#fff',
                fontWeight: "bold"
              }}
            >
              <div>
                <strong>Assigned Modules:</strong>
                <ul className="list-disc ml-4 mt-1">
                  {clinician.stages.map(stage => (
                    <li key={stage.code}>{stage.name}</li>
                  ))}
                </ul>
              </div>
            </Tooltip>
          </div>
        ))}
        {/* SLaM partial assignment: show "Assign Lead" when somebody is assigned but Lead is not.
            Legacy SSI-first patients reach this after the SSI clinician is assigned; the
            dialog then offers the Lead bundle. One-clinician patients normally never reach it
            (their Lead bundle is the first and only assignment) but the button stays correct
            if they do, because the dialog resolves the bundle from the patient's workflow. */}
        {isSlamOrg(userData?.organisation) && canAssignClinicians &&
          !patient.clinicians?.clinicianLead?.clinicianId && (
          <button
            type="button"
            onClick={() => setAssigningPatientId(patient.patientId)}
            aria-label={`Assign lead clinician to ${patient.firstName} ${patient.lastName}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium
              border border-secondary bg-white text-primary
              hover:bg-secondary/10 hover:border-secondary
              transition-colors duration-200 cursor-pointer"
          >
            <PlusCircleIcon className="w-4 h-4 text-secondary" />
            Assign Lead
          </button>
        )}
        {/* SLaM reverse partial: Lead exists but SSI is still unassigned.
            Handles the reverse of the legacy SSI-first flow — when an admin
            assigned the Lead bundle before the SSI clinician.  For one-clinician
            patients the Lead bundle already fills SSI, so this is only a repair
            path for an empty slot.  Only visible for SLaM / Urban ADHD orgs
            (isSlamOrg gate); non-SLaM orgs never reach a Lead-without-SSI state
            and never see this button. */}
        {isSlamOrg(userData?.organisation) && canAssignClinicians &&
          !!patient.clinicians?.clinicianLead?.clinicianId &&
          !patient.clinicians?.clinicianSSI?.clinicianId && (
          <button
            type="button"
            onClick={() => setAssigningPatientId(patient.patientId)}
            aria-label={`Assign SSI clinician to ${patient.firstName} ${patient.lastName}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium
              border border-secondary bg-white text-primary
              hover:bg-secondary/10 hover:border-secondary
              transition-colors duration-200 cursor-pointer"
          >
            <PlusCircleIcon className="w-4 h-4 text-secondary" />
            Assign SSI
          </button>
        )}
      </>
    )}
  </div>
</td>

                      <td className="px-3 py-2">
                    <AssessmentProgress patient={patient} />
                  </td>
                  {/* <td className="p-4 py-5">
                    <p className="text-sm text-slate-500">
                      {patient.status || "Active"}
                    </p>
                  </td> */}


                      <td className="px-3 py-2">
                    <div className="flex items-center space-x-2">
                      {overviewPath && (
                          <button
                            onClick={() => {
                              emitCaseloadPatientOpened({
                                patientId: patient.patientId,
                                trigger: 'action_button',
                                caseload: 'current',
                              });
                              // Record for the recently-viewed shortcut on the dashboard
                              onRecordPatientView?.(patient);
                              navigate(overviewPath, { state: overviewState });
                            }}
                            className="px-3 py-2 text-xs font-semibold text-white bg-violet-800 rounded-lg hover:bg-violet-700 transition-colors duration-200"
                          >
                            View Patient
                          </button>
                      )}
                      {/* <button
                        onClick={() => setEditingPatient(patient)}
                        className="p-2 text-violet-700 bg-white border border-violet-200 rounded-lg hover:bg-violet-50 transition-colors duration-200"
                        aria-label="Edit patient"
                        title="Edit patient details"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button> */}
                      <button
                        onClick={() => copyPatientIdToClipboard(patient.patientId)}
                        className={`p-2 transition-all duration-200 border rounded-lg ${
                          clickedButton === patient.patientId
                            ? 'text-white bg-blue-600 border-blue-600'
                            : 'text-blue-600 bg-white border-blue-200 hover:bg-blue-50'
                        }`}
                        aria-label="Copy patient ID"
                        title="Copy Patient ID"
                        data-tooltip-id={`action-copy-${patient.patientId}`}
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      </button>
                      <Tooltip
                        id={`action-copy-${patient.patientId}`}
                        place="top"
                        style={{
                          maxWidth: '250px',
                          whiteSpace: 'normal',
                          backgroundColor: 'rgba(34, 34, 34, 1)',
                          borderRadius: '10px',
                          color: '#fff',
                          fontWeight: 'bold'
                        }}
                        content="Copy ID"
                      />
                      <button
                        onClick={() => setArchivingPatient(patient)}
                        className="p-2 text-amber-600 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors duration-200"
                        aria-label="Archive patient"
                        title="Archive Patient"
                        data-tooltip-id={`action-archive-${patient.patientId}`}
                      >
                        <ArchiveIcon className="w-4 h-4" />
                      </button>
                      <Tooltip
                        id={`action-archive-${patient.patientId}`}
                        place="top"
                        style={{
                          maxWidth: '250px',
                          whiteSpace: 'normal',
                          backgroundColor: 'rgba(34, 34, 34, 1)',
                          borderRadius: '10px',
                          color: '#fff',
                          fontWeight: 'bold'
                        }}
                        content="Archive Patient"
                      />
                      <button
                        onClick={() => setRemovingPatient(patient)}
                        className="p-2 text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors duration-200"
                        aria-label="Remove patient"
                        data-tooltip-id={`action-delete-${patient.patientId}`}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                      <Tooltip
                        id={`action-delete-${patient.patientId}`}
                        place="top"
                        style={{
                          maxWidth: '250px',
                          whiteSpace: 'normal',
                          backgroundColor: 'rgba(34, 34, 34, 1)',
                          borderRadius: '10px',
                          color: '#fff',
                          fontWeight: 'bold'
                        }}
                        content="Remove Patient"
                      />
                    </div>
                  </td>
                </tr>
              );
            })
          )}
          </tbody>
        </table>
      </div>{/* end scrollable table region */}

        {/* Pagination — outside the overflow-auto div so it never scrolls horizontally */}
        <CondensedPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={sortedPatients.length}
          pageSize={patientsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>{/* end outer card */}
      {/* Contextual note explaining which subset is shown when a chip filter is active */}
      {activeChip && CHIP_FILTERS[activeChip] && sortedPatients.length > 0 && (
        <p className="text-xs font-medium text-gray-500 mt-1 text-left">
          {CHIP_FILTERS[activeChip].contextNote(filteredPatients.length, totalNonArchivedCount)}
        </p>
      )}

      {/* {editingPatient && (
        <PatientEditForm
          isOpen={!!editingPatient}
          onClose={() => setEditingPatient(null)}
          patient={editingPatient}
        />
      )} */}

      {/* Patient Archive Dialog — rendered unconditionally so Radix Presence
         can animate the exit transition when `open` flips to false. */}
      <PatientArchiveDialog
        isOpen={!!archivingPatient}
        onClose={() => setArchivingPatient(null)}
        patient={archivingPatient}
      />

      {/* Patient Remove Dialog — same pattern as above. */}
      <PatientRemoveForm
        isOpen={!!removingPatient}
        onClose={() => setRemovingPatient(null)}
        patient={removingPatient}
      />

      {/* Assign Clinicians Dialog — opened from the "+ Assign clinician" pill button
          in the Assigned Clinicians cell. Reuses the same dialog component used on
          the Patient Overview page. The key prop forces a clean remount per patient
          so internal step/selection state never leaks between different patients. */}
      {assigningPatientId && (
        <AssignCliniciansDialog
          key={assigningPatientId}
          isOpen={!!assigningPatientId}
          onClose={() => setAssigningPatientId(null)}
          patientId={assigningPatientId}
          patientDataModel={(() => {
            // Build a minimal data model from the patient summary so the dialog
            // can read existing assignments (e.g. SSI-only state in SLaM) and
            // filter clinicians by the patient's assessment pathway.
            const p = patientsData.find(pt => pt.patientId === assigningPatientId);
            return p ? {
              clinicians: p.clinicians || null,
              organisation: p.organisation,
              assessmentModule: p.assessmentModule || 'adultAdhd',
            } : null;
          })()}
          // The summary has no stamped workflowConfig, so hand the dialog the
          // server-computed module order instead. This is how it tells a legacy
          // SSI-first SLaM patient (SSI-vs-Lead chooser) from a one-clinician
          // CI-first patient (single Lead bundle including SSI). Absent on
          // summaries written before the field existed → the dialog falls back
          // to its own resolver.
          workflowModuleOrder={
            patientsData.find(pt => pt.patientId === assigningPatientId)?.workflowModuleOrder || null
          }
          refreshPatientData={async () => {
            // No force-refresh here — the optimistic clinician patch applied by
            // optimisticUpdatePatient inside AssignCliniciansDialog is already the
            // truth (the CF succeeded). Force-refreshing risks overwriting that
            // optimistic state with stale patient_summaries data if the Firestore
            // trigger hasn't propagated yet — the exact race condition that caused
            // clinician assignments to "disappear" on large orgs. Same no-refresh
            // pattern as archive/remove/restore (see ClinicianDashboard lines 647-650).
          }}
        />
      )}
    </div>
  );
}

export default PatientTable;